import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ROUTE_DEFINITIONS } from '@/routes';
import { employeeGet, employeeUpdate, type EmployeeProfile } from '@/api/domains/employee';
import type { Region, Commune } from '@/api/domains/geography';
import type { MaritalStatus } from '@/api/domains/catalog';
import { useAppSelector, useUI } from '@/hooks';
import { useUnsavedChangesWarning } from '@/hooks/ui/navigation';
import { processBackendValidationErrors } from '@/utils/formValidationErrors';
import {
  updateEmployeePersonalDataSchema,
  updateEmployeeContactDataSchema,
  updateEmployeeAddressDataSchema,
  type UpdateEmployeePersonalData,
  type UpdateEmployeeContactData,
  type UpdateEmployeeAddressData,
} from '@/schemas/employee';


interface UseEmployeeProfileResult {
  isLoading: boolean;
  error: string | null;
  profile: EmployeeProfile | null;
  activeTab: 'personal' | 'contact' | 'address';
  setActiveTab: (tab: 'personal' | 'contact' | 'address') => void;
  handleCancel: () => void;
  syncAssistance: boolean;
  genderOptions: { value: string; label: string }[];
  maritalStatusOptions: { value: string; label: string }[];
  employmentStatusOptions: { value: string; label: string }[];
  regionOptions: { value: string; label: string }[];
  communeOptions: { value: string; label: string }[];
  workRegionOptions: { value: string; label: string }[];
  workCommuneOptions: { value: string; label: string }[];
  // Personal data form
  personalForm: {
    register: ReturnType<typeof useForm<UpdateEmployeePersonalData>>['register'];
    handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
    setValue: ReturnType<typeof useForm<UpdateEmployeePersonalData>>['setValue'];
    watch: ReturnType<typeof useForm<UpdateEmployeePersonalData>>['watch'];
    errors: ReturnType<typeof useForm<UpdateEmployeePersonalData>>['formState']['errors'];
    isSubmitting: boolean;
  };
  // Contact data form
  contactForm: {
    register: ReturnType<typeof useForm<UpdateEmployeeContactData>>['register'];
    handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
    errors: ReturnType<typeof useForm<UpdateEmployeeContactData>>['formState']['errors'];
    isSubmitting: boolean;
  };
  // Address data form
  addressForm: {
    register: ReturnType<typeof useForm<UpdateEmployeeAddressData>>['register'];
    handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
    setValue: ReturnType<typeof useForm<UpdateEmployeeAddressData>>['setValue'];
    watch: ReturnType<typeof useForm<UpdateEmployeeAddressData>>['watch'];
    errors: ReturnType<typeof useForm<UpdateEmployeeAddressData>>['formState']['errors'];
    isSubmitting: boolean;
  };
}

// Gender options (M/F as per backend)
const GENDERS = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Femenino' },
];

export function useEmployeeProfile(): UseEmployeeProfileResult {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const { showNotification, showApiError } = useUI();
  const syncAssistance = useAppSelector((state) => state.auth.user?.syncAssistance ?? false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<EmployeeProfile | null>(null);
  const [activeTab, internalSetActiveTab] = useState<'personal' | 'contact' | 'address'>('personal');

  // Catalog states
  const [regions, setRegions] = useState<Region[]>([]);
  const [communes, setCommunes] = useState<Commune[]>([]);
  const [workCommunes, setWorkCommunes] = useState<Commune[]>([]);
  const [maritalStatuses, setMaritalStatuses] = useState<MaritalStatus[]>([]);

  // Load profile on mount
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!employeeId) {
        setError('Identificador inválido');
        setIsLoading(false);
        return;
      }
      const id = Number(employeeId);
      if (Number.isNaN(id)) {
        setError('Identificador inválido');
        setIsLoading(false);
        return;
      }
      const response = await employeeGet.getProfile(id);
      if (!mounted) return;
      if (!response.success || !response.data) {
        setError(response.error?.description || 'No se pudo cargar el perfil');
        setIsLoading(false);
        return;
      }
      setProfile(response.data);
      setIsLoading(false);
    };
    void load();
    return () => { mounted = false; };
  }, [employeeId]);

  // Load catalogs on mount (regions, marital statuses)
  useEffect(() => {
    let mounted = true;
    const loadCatalogs = async () => {
      setIsLoading(true);
      try {
        const regionsResponse = await employeeGet.getRegions('CL');
        if (mounted && regionsResponse.success && regionsResponse.data) {
          setRegions(regionsResponse.data);
        }

        const maritalStatusResponse = await employeeGet.getMaritalStatuses(1);
        if (mounted && maritalStatusResponse.success && maritalStatusResponse.data) {
          setMaritalStatuses(maritalStatusResponse.data);
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    void loadCatalogs();
    return () => { mounted = false; };
  }, []);

  const setActiveTab = useCallback((tab: 'personal' | 'contact' | 'address') => {
    internalSetActiveTab(tab);
  }, []);

  // Navigation target for cancel
  const navigateToList = useCallback(() => {
    void navigate(ROUTE_DEFINITIONS.EMPLEADOS.path);
  }, [navigate]);

  // Personal data form - use empty defaults, will be populated with reset() when profile loads
  const personalFormMethods = useForm<UpdateEmployeePersonalData>({
    resolver: zodResolver(updateEmployeePersonalDataSchema),
    mode: 'onBlur',
    defaultValues: {
      id: 0,
      firstNames: '',
      lastName: '',
      secondLastName: '',
      birthDate: '',
      genderCode: '',
      maritalStatusId: 0,
      employmentStatus: '',
      doesNotSync: false,
    },
  });

  // Contact data form - use empty defaults, will be populated with reset() when profile loads
  const contactFormMethods = useForm<UpdateEmployeeContactData>({
    resolver: zodResolver(updateEmployeeContactDataSchema),
    mode: 'onBlur',
    defaultValues: {
      id: 0,
      corporateEmail: '',
      personalEmail: '',
      companyPhoneNumber: '',
      personalPhoneNumber: '',
    },
  });

  // Address data form - use empty defaults, will be populated with reset() when profile loads
  const addressFormMethods = useForm<UpdateEmployeeAddressData>({
    resolver: zodResolver(updateEmployeeAddressDataSchema),
    mode: 'onBlur',
    defaultValues: {
      id: 0,
      address: '',
      regionId: null,
      communeId: null,
      workAddress: '',
      workRegionId: null,
      workCommuneId: null,
    },
  });

  // Calculate combined isDirty state from all forms
  const isDirty = 
    personalFormMethods.formState.isDirty || 
    contactFormMethods.formState.isDirty ||
    addressFormMethods.formState.isDirty;

  // Unsaved changes warning hook
  const { handleCancelWithWarning: handleCancel, confirmNavigation } = useUnsavedChangesWarning({
    onConfirmLeave: navigateToList,
    isDirty,
  });

  // Load communes when region changes
  const regionIdValue = addressFormMethods.watch('regionId');

  useEffect(() => {
    let mounted = true;
    const loadCommunes = async () => {
      if (!regionIdValue) {
        setCommunes([]);
        // Reset commune when region changes
        addressFormMethods.setValue('communeId', null);
        return;
      }

      try {
        const communesResponse = await employeeGet.getCommunes('CL', regionIdValue);
        if (mounted && communesResponse.success && communesResponse.data) {
          setCommunes(communesResponse.data);
        } else if (mounted) {
          setCommunes([]);
        }
      } catch {
        if (mounted) setCommunes([]);
      }
    };
    void loadCommunes();
    return () => {
      mounted = false;
    };
  }, [regionIdValue, addressFormMethods]);

  // Load work communes when work region changes
  const workRegionIdValue = addressFormMethods.watch('workRegionId');

  useEffect(() => {
    let mounted = true;
    const loadWorkCommunes = async () => {
      if (!workRegionIdValue) {
        setWorkCommunes([]);
        // Reset work commune when work region changes
        addressFormMethods.setValue('workCommuneId', null);
        return;
      }

      try {
        const communesResponse = await employeeGet.getCommunes('CL', workRegionIdValue);
        if (mounted && communesResponse.success && communesResponse.data) {
          setWorkCommunes(communesResponse.data);
        } else if (mounted) {
          setWorkCommunes([]);
        }
      } catch {
        if (mounted) setWorkCommunes([]);
      }
    };
    void loadWorkCommunes();
    return () => {
      mounted = false;
    };
  }, [workRegionIdValue, addressFormMethods]);

  // Update form values when profile changes using reset() to establish proper isDirty baseline
  useEffect(() => {
    if (profile) {
      // Use reset() instead of setValue() to establish correct isDirty baseline
      personalFormMethods.reset({
        id: profile.id,
        firstNames: profile.firstNames,
        lastName: profile.lastName,
        secondLastName: profile.secondLastName || '',
        birthDate: profile.birthDate || '',
        genderCode: profile.genderCode || '',
        maritalStatusId: profile.maritalStatusId || 0,
        employmentStatus: profile.employmentStatus || '',
        doesNotSync: profile.doesNotSync ?? false,
      });

      contactFormMethods.reset({
        id: profile.id,
        corporateEmail: profile.corporateEmail,
        personalEmail: profile.personalEmail,
        companyPhoneNumber: profile.companyPhoneNumber || '',
        personalPhoneNumber: profile.personalPhoneNumber || '',
      });

      addressFormMethods.reset({
        id: profile.id,
        address: profile.address || '',
        regionId: profile.regionId || null,
        communeId: profile.communeId || null,
        workAddress: profile.workAddress || '',
        workRegionId: profile.workRegionId || null,
        workCommuneId: profile.workCommuneId || null,
      });
    }
  }, [profile, personalFormMethods, contactFormMethods, addressFormMethods]);

  // Build field mapping for backend validation error injection (memoized)
  const buildFieldMapping = useMemo(() => ({
    // Personal tab fields
    firstNames: { tab: 'personal' as const, formInstance: personalFormMethods },
    lastName: { tab: 'personal' as const, formInstance: personalFormMethods },
    secondLastName: { tab: 'personal' as const, formInstance: personalFormMethods },
    birthDate: { tab: 'personal' as const, formInstance: personalFormMethods },
    genderCode: { tab: 'personal' as const, formInstance: personalFormMethods },
    maritalStatusId: { tab: 'personal' as const, formInstance: personalFormMethods },
    employmentStatus: { tab: 'personal' as const, formInstance: personalFormMethods },
    
    // Contact tab fields
    corporateEmail: { tab: 'contact' as const, formInstance: contactFormMethods },
    personalEmail: { tab: 'contact' as const, formInstance: contactFormMethods },
    companyPhoneNumber: { tab: 'contact' as const, formInstance: contactFormMethods },
    personalPhoneNumber: { tab: 'contact' as const, formInstance: contactFormMethods },
    
    // Address tab fields
    address: { tab: 'address' as const, formInstance: addressFormMethods },
    regionId: { tab: 'address' as const, formInstance: addressFormMethods },
    communeId: { tab: 'address' as const, formInstance: addressFormMethods },
    workAddress: { tab: 'address' as const, formInstance: addressFormMethods },
    workRegionId: { tab: 'address' as const, formInstance: addressFormMethods },
    workCommuneId: { tab: 'address' as const, formInstance: addressFormMethods },
  }), [personalFormMethods, contactFormMethods, addressFormMethods]);

  // Handle backend validation errors
  const handleBackendValidationError = useCallback((
    validationErrors: Array<{ fieldName: string; errorMessage: string }>,
  ) => {
    processBackendValidationErrors(
      validationErrors,
      buildFieldMapping,
      setActiveTab,
    );
    showNotification('error', 'Corrige los errores de validación', 4000);
  }, [buildFieldMapping, setActiveTab, showNotification]);

  // Unified update handler
  const handleUpdate = useCallback(async (employeeProfile: EmployeeProfile) => {
    try {
      const response = await employeeUpdate.update(employeeProfile);
      
      if (response.success) {
        showNotification('success', 'Empleado actualizado correctamente', 4000);
        // Mark navigation as confirmed to bypass unsaved changes warning
        confirmNavigation();
        void navigate(ROUTE_DEFINITIONS.EMPLEADOS.path);
      } else {
        if (response.error?.code === 'VALIDATION_ERROR' && response.error.validationErrors) {
          handleBackendValidationError(response.error.validationErrors);
          return;
        }
        showApiError(response);
      }
    } catch {
      showNotification('error', 'Error inesperado al actualizar empleado', 4000);
    }
  }, [navigate, showNotification, showApiError, handleBackendValidationError, confirmNavigation]);

  // Single memoized cross-validated submit handler (used by all tabs)
  const handleCrossValidatedSubmit = useCallback(
    async (event?: React.BaseSyntheticEvent) => {
      event?.preventDefault();
        
      // Validate ALL forms to detect errors in any tab
      const [personalValid, contactValid, addressValid] = await Promise.all([
        personalFormMethods.trigger(),
        contactFormMethods.trigger(),
        addressFormMethods.trigger(),
      ]);
        
      // Check each form and redirect to first error tab
      const validationChecks = [
        { valid: personalValid, tab: 'personal' as const, label: 'Datos Personales' },
        { valid: contactValid, tab: 'contact' as const, label: 'Datos de Contacto' },
        { valid: addressValid, tab: 'address' as const, label: 'Dirección' },
      ];
        
      for (const check of validationChecks) {
        if (!check.valid) {
          internalSetActiveTab(check.tab);
          showNotification('error', `Corrige los errores de validación en ${check.label}`, 4000);
          return;
        }
      }
        
      // Get current values from ALL forms
      const personalData = personalFormMethods.getValues();
      const contactData = contactFormMethods.getValues();
      const addressData = addressFormMethods.getValues();
      
      // Merge with profile to create complete updated profile
      const updatedProfile: EmployeeProfile = {
        ...profile!,
        ...personalData,
        ...contactData,
        ...addressData,
      };
        
      // Submit with complete updated profile
      await handleUpdate(updatedProfile);
    },
    [
      personalFormMethods,
      contactFormMethods,
      addressFormMethods,
      handleUpdate,
      profile,
      showNotification,
    ],
  );

  // All tabs use the same handler (avoid unnecessary function allocations)
  const handlePersonalSubmit = handleCrossValidatedSubmit;
  const handleContactSubmit = handleCrossValidatedSubmit;
  const handleAddressSubmit = handleCrossValidatedSubmit;

  // Transform to selector options
  const regionOptions = useMemo(
    () => regions.map(region => ({ value: String(region.id), label: region.name })),
    [regions],
  );

  const communeOptions = useMemo(
    () => communes.map(commune => ({ value: String(commune.id), label: commune.name })),
    [communes],
  );

  // Work regions are the same as personal regions
  const workRegionOptions = regionOptions;

  const workCommuneOptions = useMemo(
    () => workCommunes.map(commune => ({ value: String(commune.id), label: commune.name })),
    [workCommunes],
  );

  const maritalStatusOptions = useMemo(
    () => maritalStatuses.map(status => ({ value: String(status.id), label: status.description })),
    [maritalStatuses],
  );

  // Employment status options (V=Activo, N=Inactivo)
  const employmentStatusOptions = useMemo(
    () => [
      { value: 'V', label: translate('employee:listView.status.active') },
      { value: 'N', label: translate('employee:listView.status.inactive') },
    ],
    [translate],
  );

  return {
    isLoading,
    error,
    profile,
    activeTab,
    setActiveTab,
    handleCancel,
    syncAssistance,
    genderOptions: GENDERS,
    maritalStatusOptions,
    employmentStatusOptions,
    regionOptions,
    communeOptions,
    workRegionOptions,
    workCommuneOptions,
    personalForm: {
      register: personalFormMethods.register,
      handleSubmit: handlePersonalSubmit,
      setValue: personalFormMethods.setValue,
      watch: personalFormMethods.watch,
      errors: personalFormMethods.formState.errors,
      isSubmitting: personalFormMethods.formState.isSubmitting,
    },
    contactForm: {
      register: contactFormMethods.register,
      handleSubmit: handleContactSubmit,
      errors: contactFormMethods.formState.errors,
      isSubmitting: contactFormMethods.formState.isSubmitting,
    },
    addressForm: {
      register: addressFormMethods.register,
      handleSubmit: handleAddressSubmit,
      setValue: addressFormMethods.setValue,
      watch: addressFormMethods.watch,
      errors: addressFormMethods.formState.errors,
      isSubmitting: addressFormMethods.formState.isSubmitting,
    },
  };
}

export default useEmployeeProfile;
